import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit, Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from "@angular/core";
import {Base} from "../../../../../../../../common/interfaces/base.interfaces";
import {ConfirmComponent} from "../../../../../../../../common/components/confirm/confirm.component";
import {Warehouse} from "../../../../../../../../common/interfaces/warehouse.interface";
import {Api} from "../../../../../../../../common/services/api.service";
import {ToastService} from "../../../../../../../../common/services/toast.service";
import {SpinnerService} from "../../../../../../../../common/services/spinner.service";
import {Api3Service} from "../../../../../../../../common/services/api3.service";
import {SectionTransactionSplitModalComponent} from "../../../../../../common/components/transactions";
import {Modal, ModalService} from "../../../../../../../services/modal.service";
import {Table} from "../../../../../../../../common/interfaces/table.interface";
import {Order} from "../../../../../../../../common/interfaces/order.interface";
import {Table2Component} from "../../../../../../../../common/components/table2";
import {CommonFormComponent} from "../../../../../../../../common/components/form";
import {Form} from "../../../../../../../../common/interfaces/form.interface";
import ISelectOption = Form.ISelectOption;

@Component({
    selector: "section-warehouse-order-wizard-shuffle-items",
    template: `
        <common-table2 *ngIf="tableSettings" [settings]="tableSettings" (onRowsSelected)="selectedRows = $event">
            <div row1>
                <button mat-raised-button type="button" [disabled]="!selectedRows.length" color="accent"
                        (click)="linkSelected()">
                    Link selected
                </button>
            </div>
        </common-table2>
        <common-confirm></common-confirm>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class WarehouseProceduresWizardShuffleItemsComponent implements OnInit, OnDestroy, OnChanges {

    protected destroy$: EventEmitter<boolean> = new EventEmitter(false);

    public selectedRows: any[] = [];

    @ViewChild(ConfirmComponent, {static: true})
    public confirmRef: ConfirmComponent;

    @ViewChild(Table2Component, {static: false})
    public tableRef: Table2Component;

    @Input()
    public shipment: Warehouse.IShipment;

    @Input()
    public packages: Warehouse.IPackage[];

    @Input()
    public state: Base.IState;

    @Input()
    public selected_package_name: string = null;

    @Output()
    public onUpdate: EventEmitter<boolean> = new EventEmitter(true);


    public tableSettings: Table.ISettings;

    public constructor(
        protected changeDetectorRef: ChangeDetectorRef,
        private api3Service: Api3Service,
        private toastService: ToastService,
        private spinnerService: SpinnerService,
        private modalService: ModalService,
    ) {
    }

    private url(): Table.ITableApi {
        return {
            url: [this.state.section, "shipments", "" + this.shipment.id, "transactions"],
            query: {
                relations: [
                    "InventoryConversion:id,customers_inventory_name,customers_sub_inventory",
                    "WarehousePackage:id,name"
                ],
                warehouse_package: this.selected_package_name ?? null
            },
            version: 3
        };
    }

    private prepareTable(): void {
        this.tableSettings = {
            api: this.url(),
            columns: [
                {
                    data: "warehouse_package.name",
                    name: "WarehousePackage.name",
                    title: "Package",
                    sortable: false,
                    render: (row: any): string => {
                        return row.warehouse_package?.name ?? "NOT LINKED";
                    },
                    cssClassFn: (row: any): string => {
                        return !row.warehouse_package ? "text-error" : "";
                    }
                },
                {
                    data: "item",
                    title: "Item"
                },
                {
                    data: "serial",
                    title: "Serial"
                },
                {
                    data: "inventory_conversion.customers_inventory_name",
                    title: "Hub",
                    searchable: false,
                    sortable: false,
                    render: (row: any): string => {
                        return row.inventory_conversion.customers_inventory_name
                            + " (" + row.inventory_conversion.customers_sub_inventory + ")";
                    }
                },
                {
                    data: "quantity",
                    title: "Quantity",
                    searchable: false,
                },
                {
                    data: "ref",
                    title: "Order",
                }
            ],
            actions: [
                {
                    name: "link",
                    title: "Link",
                    click: (row: any): void => {
                        this.linkTransaction(row);
                    }
                },
                {
                    name: "call_split",
                    title: "Split",
                    click: (row: any): void => {
                        this.splitTransaction(row);
                    },
                    disabledFn: (row: any): boolean => {
                        return row.quantity <= 1;
                    }
                }
            ],
            multi_select: true
        };
    }

    private async selectPackage(): Promise<string> {
        const res: Modal.IResponse = await this.modalService.open(CommonFormComponent, {
            formConfig: {
                name: "Select package to link boxes to",
                description: "",
                fields: [
                    {
                        label: "Package name",
                        name: "size",
                        size: "full",
                        type: "select",
                        values: this.packages
                            .filter((p: Warehouse.IPackage): boolean => {
                                return p.name !== this.selected_package_name && p.shipment_id === this.shipment.id;
                            })
                            .map((p: Warehouse.IPackage): ISelectOption => ({
                                name: p.name,
                                value: p.name
                            })),
                        selected_on_top: false,
                        required: true,
                    },
                ],
            },
            noReset: true
        });

        return res.value ?? null;
    }

    public async linkTransaction(
        transaction: Warehouse.ITransaction,
        warehouse_package_name: string = null,
        sendUpdate: boolean = true
    ): Promise<any> {

        if (!warehouse_package_name) {
            warehouse_package_name = await this.selectPackage();
        }

        if (!warehouse_package_name) {
            return;
        }

        this.spinnerService.show();
        const {code, message}: Api.IResponse = await this.api3Service.put(
            `${this.state.section}/warehouse-transactions/${transaction.id}/package`,
            {warehouse_package_name}
        );

        this.spinnerService.hide();

        if (code === 200) {
            this.toastService.show(message, "success");
            this.tableRef.reload();

            if (sendUpdate) {
                this.onUpdate.emit(true);
            }
        }
    }

    public async linkSelected(): Promise<any> {
        const warehouse_package_name: string = await this.selectPackage();

        if (!warehouse_package_name) {
            return;
        }

        for (const row of this.selectedRows) {
            this.linkTransaction(row, warehouse_package_name, false);
        }

        this.onUpdate.emit(true);
    }

    public splitTransaction(transaction: Warehouse.ITransaction): void {
        this.modalService.open(SectionTransactionSplitModalComponent, {
            transaction
        }).then((res: Modal.IResponse) => {
            if (res) {
                this.tableRef.reload();
            }
        });
    }

    public ngOnInit(): void {
        this.prepareTable();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.tableSettings
            && changes.selected_package_name
            && changes.selected_package_name.currentValue !== changes.selected_package_name.previousValue
        ) {
            this.tableRef.reload(this.url());
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

}
